import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { bulkIgnoreDraftMasterMenuProducts, bulkPublishDraftMasterMenuProducts, getDraftMasterMenuProduct, getDraftMasterMenuProducts } from "@/api/admin.js";
import BulkPublishProgressBanner from "./BulkPublishProgressBanner.jsx";
import { isPublishJobTerminal, usePublishJobTracker } from "./usePublishJobTracker.js";
import { getAdminAvailableCategories } from "@/api/masterMenu.js";
import { DangerButton, PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import Loader from "@/components/Loader.jsx";
import ModalBasic from "@/components/ModalBasic.jsx";
import PaginationNumeric from "@/components/PaginationNumeric.jsx";
import { addToast } from "@/store/app/action.js";
import MasterMenuCreateProduct from "./MasterMenuCreateProduct.jsx";
import DraftProductsUpcCsvModal from "./DraftProductsUpcCsvModal.jsx";
import { MockListingPreview, previewFromDraft } from "./MasterMenuListingPreview.jsx";
import {
  draftRecordToFormDraft,
  isDraftUnmatchedCategory,
  normalizeAvailableCategories,
  publishMasterMenuDraft,
  splitDraftsForBulkPublish,
} from "./masterMenuPublish.js";

const DEFAULT_LIMIT = 50;

const SORT_UPDATED_DESC = "updated_desc";
const SORT_CREATED_DESC = "created_desc";
const SORT_TITLE_ASC = "title_asc";

const VIEW_ACTIVE = "active";
const VIEW_IGNORED = "ignored";
const VIEW_ALL = "all";

const BULK_CONFIRM_PUBLISH = "publish";
const BULK_CONFIRM_PUBLISH_IGNORE_REST = "publish_ignore_rest";
const BULK_CONFIRM_IGNORE = "ignore";
const BULK_CONFIRM_RESTORE = "restore";

const BULK_LIST_PREVIEW_MAX = 8;

function formatTimestamp(iso) {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const sortPillClass = (active) =>
  `inline-flex items-center justify-center text-xs font-medium leading-5 rounded-full px-3 py-1 border shadow-sm duration-150 ease-in-out ${
    active
      ? "border-theme-button-primary bg-theme-button-primary text-theme-button-primary-text"
      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
  }`;

function FixedDropdown({
  show,
  onClose,
  triggerRef,
  menuRef,
  estimatedHeight,
  align,
  children,
}) {
  const [pos, setPos] = useState({ top: 0, left: 0, right: undefined });

  useEffect(() => {
    if (!show || !triggerRef?.current) return;
    const btn = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const menuWidth = 224;
    const h = estimatedHeight;

    const wouldOverflowBottom = btn.bottom + h > vh;
    const top = wouldOverflowBottom ? btn.top - h : btn.bottom;

    if (align === "right") {
      setPos({
        top,
        right: vw - btn.right,
        left: undefined,
      });
    } else {
      let left = btn.left;
      if (left + menuWidth > vw - 8) left = Math.max(8, vw - menuWidth - 8);
      if (left < 8) left = 8;
      setPos({ top, left, right: undefined });
    }
  }, [show, triggerRef, estimatedHeight, align]);

  useEffect(() => {
    const handleOutside = (e) => {
      const inMenu = menuRef.current?.contains(e.target);
      const inTrigger = triggerRef.current?.contains(e.target);
      if (!inMenu && !inTrigger) onClose();
    };
    if (show) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [show, onClose, menuRef, triggerRef]);

  if (!show) return null;

  return (
    <div
      ref={menuRef}
      className="fixed w-56 py-2 z-50 bg-white dark:bg-theme-surface-accent rounded-sm shadow-lg border border-slate-200 dark:border-slate-700"
      style={{
        top: `${pos.top}px`,
        ...(pos.right != null ? { right: `${pos.right}px` } : { left: `${pos.left}px` }),
      }}
    >
      {children}
    </div>
  );
}

const viewPillClass = sortPillClass;

function isDraftSelectable(row) {
  const status = String(row?.publish_status || "").trim();
  return status !== "queued" && status !== "processing";
}

function isDraftPublishable(row, availableCategories = []) {
  return (
    isDraftSelectable(row) &&
    !row?.ignored &&
    !isDraftUnmatchedCategory(row, availableCategories)
  );
}

function publishStatusClass(status) {
  const s = String(status || "").trim();
  if (s === "failed") return "text-red-600 dark:text-red-400";
  if (s === "queued" || s === "processing") return "text-indigo-600 dark:text-indigo-300";
  return "text-slate-500 dark:text-slate-400";
}

function publishStatusLabel(status) {
  const s = String(status || "").trim();
  if (s === "queued") return "Queued";
  if (s === "processing") return "Publishing…";
  if (s === "failed") return "Publish failed";
  return "";
}

function productLabel(row) {
  const title = String(row?.title || "").trim();
  const subtitle = String(row?.subtitle || "").trim();
  if (title && subtitle) return `${title} — ${subtitle}`;
  return title || subtitle || "—";
}

function draftProductImageUrl(row) {
  return String(row?.image_large || row?.image || row?.image_small || "").trim();
}

function BulkConfirmDraftList({ rows, max = BULK_LIST_PREVIEW_MAX, subtitleForRow }) {
  const shown = rows.slice(0, max);
  const rest = rows.length - shown.length;
  if (!rows.length) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">No items selected.</p>;
  }
  return (
    <ul className="divide-y divide-slate-200 dark:divide-slate-700 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/30 max-h-52 overflow-y-auto">
      {shown.map((row) => {
        const imageUrl = draftProductImageUrl(row);
        const subtitle =
          typeof subtitleForRow === "function"
            ? subtitleForRow(row)
            : [row.company, row.upc_code].filter(Boolean).join(" · ") || "—";
        return (
          <li key={row.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="h-9 w-9 shrink-0 rounded object-cover bg-slate-100 dark:bg-slate-800"
              />
            ) : (
              <div className="h-9 w-9 shrink-0 rounded bg-slate-100 dark:bg-slate-800" aria-hidden />
            )}
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800 dark:text-slate-100 truncate">
                {productLabel(row)}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{subtitle}</p>
            </div>
          </li>
        );
      })}
      {rest > 0 ? (
        <li className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
          + {rest} more not shown
        </li>
      ) : null}
    </ul>
  );
}

function bulkPublishSkippedSubtitle(row) {
  const category = String(row?.type || "UNMATCHED").trim() || "UNMATCHED";
  const upc = String(row?.upc_code || "").trim();
  return [category, upc].filter(Boolean).join(" · ");
}

function draftDetailToPreviewRecord(detail) {
  if (!detail) return null;
  const primary = String(detail.image_large || detail.image || detail.image_small || "").trim();
  return {
    title: detail.title,
    subtitle: detail.subtitle,
    volume_label: detail.volume_label,
    description: detail.description,
    image: primary,
    image_large: primary,
    image_small: String(detail.image_small || detail.image || primary).trim(),
    additional_images: Array.isArray(detail.additional_images) ? detail.additional_images : [],
  };
}

const MasterMenuDraftProducts = () => {
  const dispatch = useDispatch();
  const { selectedLocation } = useSelector((state) => state.app);
  const [page, setPage] = useState(1);
  const [sortMode, setSortMode] = useState(SORT_UPDATED_DESC);
  const [viewFilter, setViewFilter] = useState(VIEW_ACTIVE);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [draftDetail, setDraftDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [bulkPublishLoading, setBulkPublishLoading] = useState(false);
  const [bulkIgnoreLoading, setBulkIgnoreLoading] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);

  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [upcCsvOpen, setUpcCsvOpen] = useState(false);
  const bulkBtnRef = useRef(null);
  const bulkMenuRef = useRef(null);
  const headerCheckboxRef = useRef(null);

  useEffect(() => {
    if (!selectedLocation) return;
    let cancelled = false;
    getAdminAvailableCategories()
      .then((data) => {
        if (!cancelled) setAvailableCategories(normalizeAvailableCategories(data));
      })
      .catch(() => {
        if (!cancelled) setAvailableCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLocation]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, sortMode, viewFilter]);

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [page, debouncedQ, sortMode, viewFilter]);

  const fetchPage = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const res = await getDraftMasterMenuProducts({
        page,
        limit: DEFAULT_LIMIT,
        q: debouncedQ || undefined,
        sort: sortMode,
        ignored: viewFilter,
      });
      setPayload(res?.data ?? null);
    } catch (e) {
      console.error(e);
      if (!silent) {
        setError(e?.response?.data?.message || e.message || "Failed to load draft products");
        setPayload(null);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [page, debouncedQ, sortMode, viewFilter]);

  const refreshDraftList = useCallback(() => {
    fetchPage({ silent: true });
  }, [fetchPage]);

  const handlePublishJobTerminal = useCallback(
    (job) => {
      refreshDraftList();
      const succeeded = Number(job?.succeeded) || 0;
      const failed = Number(job?.failed) || 0;
      if (failed === 0 && succeeded > 0) {
        dispatch(
          addToast({
            type: "success",
            message: `Published ${succeeded} draft${succeeded === 1 ? "" : "s"}.`,
            timer: 5,
          })
        );
      } else if (failed > 0 && succeeded > 0) {
        dispatch(
          addToast({
            type: "warning",
            message: `Published ${succeeded}, ${failed} failed. Check the list for failed drafts.`,
            timer: 8,
          })
        );
      } else if (failed > 0) {
        dispatch(
          addToast({
            type: "error",
            message: `Publish batch failed for all ${failed} draft${failed === 1 ? "" : "s"}.`,
            timer: 8,
          })
        );
      }
    },
    [dispatch, refreshDraftList]
  );

  const {
    jobId: activePublishJobId,
    job: activePublishJob,
    collapsed: publishBannerCollapsed,
    setCollapsed: setPublishBannerCollapsed,
    pollError: publishPollError,
    isActive: publishJobActive,
    startJob: startPublishJob,
    dismissJob: dismissPublishJob,
  } = usePublishJobTracker({
    onPollTick: refreshDraftList,
  });

  const terminalToastSentRef = useRef(false);
  const sawActivePublishJobRef = useRef(false);

  useEffect(() => {
    if (!activePublishJobId) {
      sawActivePublishJobRef.current = false;
      terminalToastSentRef.current = false;
    }
  }, [activePublishJobId]);

  useEffect(() => {
    const status = activePublishJob?.status;
    if (!status) return;

    if (!isPublishJobTerminal(status)) {
      sawActivePublishJobRef.current = true;
      return;
    }

    if (sawActivePublishJobRef.current && !terminalToastSentRef.current) {
      terminalToastSentRef.current = true;
      handlePublishJobTerminal(activePublishJob);
    }
  }, [activePublishJob, handlePublishJobTerminal]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const loadDraftDetail = useCallback(async (id) => {
    setDetailLoading(true);
    setDetailError(null);
    setDraftDetail(null);
    try {
      const res = await getDraftMasterMenuProduct(id);
      setDraftDetail(res?.data ?? null);
    } catch (e) {
      console.error(e);
      setDetailError(e?.response?.data?.message || e.message || "Failed to load draft product");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closePreviewModal = useCallback(() => {
    setPreviewOpen(false);
    setDraftDetail(null);
    setDetailError(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditOpen(false);
    setDraftDetail(null);
    setDetailError(null);
  }, []);

  const handleRowClick = useCallback(
    (row) => {
      const id = String(row?.id || "").trim();
      if (!id) return;
      setPreviewOpen(true);
      loadDraftDetail(id);
    },
    [loadDraftDetail]
  );

  const handleManualEdit = useCallback(() => {
    if (!draftDetail) return;
    setPreviewOpen(false);
    setEditOpen(true);
  }, [draftDetail]);

  const handlePublish = useCallback(async () => {
    if (!draftDetail || publishLoading) return;
    const draftId = String(draftDetail.id || "").trim();
    if (!draftId) return;

    setPublishLoading(true);
    try {
      const created = await publishMasterMenuDraft({
        draftId,
        draft: draftRecordToFormDraft(draftDetail),
        availableCategories,
        draftSkuId: String(draftDetail.sku_id || "").trim(),
      });
      dispatch(
        addToast({
          type: "success",
          message: created?.sku_id
            ? `Published (${created.sku_id})`
            : "Product published.",
          timer: 5,
        })
      );
      closePreviewModal();
      fetchPage();
    } catch (e) {
      if (e?.createdProduct?.sku_id) {
        dispatch(
          addToast({
            type: "warning",
            message: e.message,
            timer: 8,
          })
        );
        closePreviewModal();
        fetchPage();
        return;
      }
      dispatch(
        addToast({
          type: "error",
          message: e?.message || "Publish failed.",
          timer: 8,
        })
      );
    } finally {
      setPublishLoading(false);
    }
  }, [draftDetail, publishLoading, availableCategories, dispatch, closePreviewModal, fetchPage]);

  const handlePublished = useCallback(() => {
    closeEditModal();
    fetchPage();
  }, [closeEditModal, fetchPage]);

  const handleBulkPublish = useCallback(
    async ({ ignoreRemaining = false, ids: idsOverride, ignoreExemptIds = [] } = {}) => {
      const ids = (idsOverride ?? [...selectedKeys]).filter(Boolean);
      if (!ids.length || bulkPublishLoading) return false;

      setBulkPublishLoading(true);
      try {
        const res = await bulkPublishDraftMasterMenuProducts(ids, {
          ignoreRemaining,
          scopeQ: debouncedQ,
          ignoreExemptDraftIds: ignoreExemptIds,
        });
        const data = res?.data ?? null;
        const job = data?.job ?? data;
        const ignoredCount = Number(data?.ignored_count) || 0;
        if (job?.id) {
          startPublishJob(job.id, job);
        } else {
          dispatch(
            addToast({
              type: "warning",
              message: "Drafts queued, but progress could not be tracked. Refresh to see status.",
              timer: 6,
            })
          );
        }
        if (ignoreRemaining && ignoredCount > 0) {
          dispatch(
            addToast({
              type: "success",
              message: `Ignored ${ignoredCount} other draft${ignoredCount === 1 ? "" : "s"}.`,
              timer: 5,
            })
          );
        }
        setSelectedKeys(new Set());
        setBulkOpen(false);
        fetchPage({ silent: true });
        return true;
      } catch (e) {
        dispatch(
          addToast({
            type: "error",
            message: e?.response?.data?.message || e.message || "Bulk publish failed.",
            timer: 8,
          })
        );
        return false;
      } finally {
        setBulkPublishLoading(false);
      }
    },
    [selectedKeys, bulkPublishLoading, dispatch, fetchPage, startPublishJob, debouncedQ]
  );

  const handleBulkIgnore = useCallback(
    async (ignored, idsOverride) => {
      const ids = (idsOverride ?? [...selectedKeys]).filter(Boolean);
      if (!ids.length || bulkIgnoreLoading) return false;

      setBulkIgnoreLoading(true);
      try {
        const res = await bulkIgnoreDraftMasterMenuProducts(ids, ignored);
        const updated = Number(res?.data?.updated) || 0;
        dispatch(
          addToast({
            type: "success",
            message: ignored
              ? `Ignored ${updated} draft${updated === 1 ? "" : "s"}.`
              : `Restored ${updated} draft${updated === 1 ? "" : "s"}.`,
            timer: 5,
          })
        );
        setSelectedKeys(new Set());
        setBulkOpen(false);
        fetchPage({ silent: true });
        return true;
      } catch (e) {
        dispatch(
          addToast({
            type: "error",
            message: e?.response?.data?.message || e.message || "Bulk update failed.",
            timer: 8,
          })
        );
        return false;
      } finally {
        setBulkIgnoreLoading(false);
      }
    },
    [selectedKeys, bulkIgnoreLoading, dispatch, fetchPage]
  );

  const handleUnignoreOne = useCallback(
    (row, e) => {
      e?.stopPropagation?.();
      const id = String(row?.id || "").trim();
      if (!id || bulkIgnoreLoading) return;
      setBulkConfirm({ type: BULK_CONFIRM_RESTORE, rowOverride: row });
    },
    [bulkIgnoreLoading]
  );

  const items = payload?.items ?? [];
  const total = payload?.total ?? 0;
  const totalPages = Math.max(1, payload?.total_pages ?? 1);

  const visibleIds = useMemo(
    () =>
      items
        .filter(isDraftSelectable)
        .map((r) => String(r.id || "").trim())
        .filter(Boolean),
    [items]
  );

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedKeys.has(id));
  const someVisibleSelected =
    visibleIds.some((id) => selectedKeys.has(id)) && !allVisibleSelected;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const toggleOne = useCallback((id) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allVisibleSelected, visibleIds]);

  const selectedRows = useMemo(
    () => items.filter((r) => selectedKeys.has(String(r.id || "").trim())),
    [items, selectedKeys]
  );
  const selectedPublishCount = selectedRows.filter((r) =>
    isDraftPublishable(r, availableCategories)
  ).length;
  const selectedIgnoreCount = selectedRows.filter((r) => isDraftSelectable(r) && !r.ignored).length;
  const selectedUnignoreCount = selectedRows.filter((r) => r.ignored).length;
  const bulkConfirmBusy = bulkPublishLoading || bulkIgnoreLoading;

  const openBulkConfirm = useCallback((type) => {
    setBulkOpen(false);
    setBulkConfirm({ type });
  }, []);

  const closeBulkConfirm = useCallback(() => {
    if (!bulkConfirmBusy) {
      setBulkConfirm(null);
    }
  }, [bulkConfirmBusy]);

  const bulkConfirmRows = useMemo(() => {
    if (!bulkConfirm) return [];
    if (bulkConfirm.rowOverride) return [bulkConfirm.rowOverride];
    switch (bulkConfirm.type) {
      case BULK_CONFIRM_PUBLISH:
      case BULK_CONFIRM_PUBLISH_IGNORE_REST:
        return selectedRows.filter(
          (r) => isDraftSelectable(r) && !r.ignored
        );
      case BULK_CONFIRM_IGNORE:
        return selectedRows.filter((r) => isDraftSelectable(r) && !r.ignored);
      case BULK_CONFIRM_RESTORE:
        return selectedRows.filter((r) => r.ignored);
      default:
        return [];
    }
  }, [bulkConfirm, selectedRows]);

  const bulkPublishSplit = useMemo(() => {
    if (
      !bulkConfirm ||
      (bulkConfirm.type !== BULK_CONFIRM_PUBLISH &&
        bulkConfirm.type !== BULK_CONFIRM_PUBLISH_IGNORE_REST)
    ) {
      return { publishable: [], unmatched: [] };
    }
    const candidates = bulkConfirm.rowOverride
      ? [bulkConfirm.rowOverride]
      : bulkConfirmRows;
    return splitDraftsForBulkPublish(candidates, availableCategories);
  }, [bulkConfirm, bulkConfirmRows, availableCategories]);

  const bulkConfirmPublishRows = bulkPublishSplit.publishable;
  const bulkConfirmUnmatchedRows = bulkPublishSplit.unmatched;

  const bulkConfirmIds = useMemo(() => {
    if (
      bulkConfirm?.type === BULK_CONFIRM_PUBLISH ||
      bulkConfirm?.type === BULK_CONFIRM_PUBLISH_IGNORE_REST
    ) {
      return bulkConfirmPublishRows.map((r) => String(r.id || "").trim()).filter(Boolean);
    }
    return bulkConfirmRows.map((r) => String(r.id || "").trim()).filter(Boolean);
  }, [bulkConfirm?.type, bulkConfirmPublishRows, bulkConfirmRows]);

  const ignoreRestEstimate = useMemo(() => {
    if (bulkConfirm?.type !== BULK_CONFIRM_PUBLISH_IGNORE_REST) return 0;
    return Math.max(
      0,
      Number(total) - bulkConfirmPublishRows.length - bulkConfirmUnmatchedRows.length
    );
  }, [bulkConfirm?.type, total, bulkConfirmPublishRows.length, bulkConfirmUnmatchedRows.length]);

  const bulkPublishSkippedSection = (skippedCount, { mentionIgnoreRest = false } = {}) => {
    if (skippedCount <= 0) return null;
    const noun = skippedCount === 1 ? "draft" : "drafts";
    return (
      <div className="space-y-2">
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
          {skippedCount} {noun} with an <span className="font-semibold">UNMATCHED</span> category will
          not be published. Open each draft, set a real default category, then publish from the
          edit screen or try bulk publish again.
          {mentionIgnoreRest ? (
            <>
              {" "}
              They will stay on the active list and will not be ignored.
            </>
          ) : null}
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Skipped — needs category ({skippedCount})
          </p>
          <BulkConfirmDraftList
            rows={bulkConfirmUnmatchedRows}
            subtitleForRow={bulkPublishSkippedSubtitle}
          />
        </div>
      </div>
    );
  };

  const bulkConfirmMeta = useMemo(() => {
    if (!bulkConfirm) return null;

    const isPublishConfirm =
      bulkConfirm.type === BULK_CONFIRM_PUBLISH ||
      bulkConfirm.type === BULK_CONFIRM_PUBLISH_IGNORE_REST;
    const publishCount = isPublishConfirm ? bulkConfirmPublishRows.length : bulkConfirmRows.length;
    const skippedCount = isPublishConfirm ? bulkConfirmUnmatchedRows.length : 0;
    const noun = publishCount === 1 ? "draft" : "drafts";
    const searchScope = debouncedQ
      ? `matching “${debouncedQ}”`
      : "in the active list";

    switch (bulkConfirm.type) {
      case BULK_CONFIRM_PUBLISH:
        return {
          title:
            skippedCount > 0
              ? `Publish ${publishCount} of ${publishCount + skippedCount} selected?`
              : `Publish ${publishCount} ${noun}?`,
          primaryLabel: "Queue for publish",
          confirmVariant: "primary",
          canConfirm: publishCount > 0,
          body: (
            <div className="space-y-4">
              {publishCount > 0 ? (
                <>
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    These drafts will be published in the background — creating live products,
                    uploading images, and linking merchants. Track progress in the banner above the
                    list.
                  </p>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                      To publish ({publishCount})
                    </p>
                    <BulkConfirmDraftList rows={bulkConfirmPublishRows} />
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  None of the selected drafts can be bulk published until a real category is assigned.
                </p>
              )}
              {bulkPublishSkippedSection(skippedCount)}
              {publishCount > 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Drafts stay in the list with a “Publishing…” status until done. Failed publishes
                  remain so you can fix and retry.
                </p>
              ) : null}
            </div>
          ),
        };
      case BULK_CONFIRM_PUBLISH_IGNORE_REST:
        return {
          title:
            skippedCount > 0
              ? `Publish ${publishCount} selected and ignore the rest?`
              : "Publish selected and ignore the rest?",
          primaryLabel: "Publish and ignore rest",
          confirmVariant: "danger",
          canConfirm: publishCount > 0,
          body: (
            <div className="space-y-4">
              {publishCount > 0 ? (
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {publishCount} {publishCount === 1 ? "draft" : "drafts"} will be published. Every
                  other active draft {searchScope} will be moved to{" "}
                  <span className="font-semibold">Ignored</span> so you can focus on this batch.
                </p>
              ) : (
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  No selected drafts can be published. Assign categories first, or use Ignore to clear
                  the rest of the list.
                </p>
              )}
              {publishCount > 0 ? (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                    Will publish ({publishCount})
                  </p>
                  <BulkConfirmDraftList rows={bulkConfirmPublishRows} />
                </div>
              ) : null}
              {bulkPublishSkippedSection(skippedCount, { mentionIgnoreRest: true })}
              {publishCount > 0 && ignoreRestEstimate > 0 ? (
                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                  <span className="font-semibold">{ignoreRestEstimate.toLocaleString()}</span> other
                  active {ignoreRestEstimate === 1 ? "draft" : "drafts"} {searchScope} will be
                  ignored. UNMATCHED drafts in your selection are not included in that count.
                </div>
              ) : null}
              {publishCount > 0 && ignoreRestEstimate === 0 ? (
                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                  No other active drafts {searchScope} — only the selected publishable items will run.
                </div>
              ) : null}
              {publishCount > 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Ignored drafts are not deleted. Restore them anytime from the Ignored tab.
                </p>
              ) : null}
            </div>
          ),
        };
      case BULK_CONFIRM_IGNORE:
        return {
          title: `Ignore ${bulkConfirmRows.length} ${bulkConfirmRows.length === 1 ? "draft" : "drafts"}?`,
          primaryLabel: "Ignore drafts",
          confirmVariant: "danger",
          canConfirm: bulkConfirmRows.length > 0,
          body: (
            <div className="space-y-4">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Ignored drafts are hidden from the active list. They are not published or deleted —
                you can review and restore them from the <span className="font-semibold">Ignored</span> tab.
              </p>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                  To ignore ({bulkConfirmRows.length})
                </p>
                <BulkConfirmDraftList rows={bulkConfirmRows} />
              </div>
            </div>
          ),
        };
      case BULK_CONFIRM_RESTORE:
        return {
          title:
            bulkConfirmRows.length === 1
              ? "Restore draft?"
              : `Restore ${bulkConfirmRows.length} drafts?`,
          primaryLabel: "Restore to active list",
          confirmVariant: "primary",
          canConfirm: bulkConfirmRows.length > 0,
          body: (
            <div className="space-y-4">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Restored drafts return to the active list where you can preview, edit, or publish
                them.
              </p>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                  To restore ({bulkConfirmRows.length})
                </p>
                <BulkConfirmDraftList rows={bulkConfirmRows} />
              </div>
            </div>
          ),
        };
      default:
        return null;
    }
  }, [
    bulkConfirm,
    bulkConfirmRows,
    bulkConfirmPublishRows,
    bulkConfirmUnmatchedRows,
    debouncedQ,
    ignoreRestEstimate,
  ]);

  const executeBulkConfirm = useCallback(async () => {
    if (!bulkConfirm || !bulkConfirmIds.length) return;
    const { type, rowOverride } = bulkConfirm;

    let ok = false;
    const unmatchedIds = bulkConfirmUnmatchedRows
      .map((r) => String(r.id || "").trim())
      .filter(Boolean);
    if (type === BULK_CONFIRM_PUBLISH) {
      ok = await handleBulkPublish({ ids: bulkConfirmIds });
    } else if (type === BULK_CONFIRM_PUBLISH_IGNORE_REST) {
      ok = await handleBulkPublish({
        ignoreRemaining: true,
        ids: bulkConfirmIds,
        ignoreExemptIds: unmatchedIds,
      });
    } else if (type === BULK_CONFIRM_IGNORE) {
      ok = await handleBulkIgnore(true, bulkConfirmIds);
    } else if (type === BULK_CONFIRM_RESTORE) {
      ok = await handleBulkIgnore(false, bulkConfirmIds);
      if (ok && rowOverride && previewOpen) {
        setDraftDetail((d) => (d ? { ...d, ignored: false } : d));
      }
    }

    if (ok) {
      setBulkConfirm(null);
    }
  }, [
    bulkConfirm,
    bulkConfirmIds,
    bulkConfirmUnmatchedRows,
    handleBulkPublish,
    handleBulkIgnore,
    previewOpen,
  ]);

  const bulkMenuBusy = bulkPublishLoading || bulkIgnoreLoading;
  const bulkMenuHeight =
    viewFilter === VIEW_IGNORED ? 56 : viewFilter === VIEW_ALL ? 160 : 132;
  const selectedCount = selectedKeys.size;

  useEffect(() => {
    if (selectedCount === 0) setBulkOpen(false);
  }, [selectedCount]);

  const summary = useMemo(() => {
    if (total === 0) {
      if (viewFilter === VIEW_IGNORED) return "No ignored drafts";
      if (viewFilter === VIEW_ACTIVE) return "No active drafts";
      return "No drafts";
    }
    const start = (page - 1) * DEFAULT_LIMIT + 1;
    const end = Math.min(page * DEFAULT_LIMIT, Number(total));
    const scope =
      viewFilter === VIEW_IGNORED ? "ignored" : viewFilter === VIEW_ACTIVE ? "active" : "";
    const prefix = scope ? `${scope} · ` : "";
    return `${prefix}${start.toLocaleString()}–${end.toLocaleString()} of ${Number(total).toLocaleString()}`;
  }, [total, page, viewFilter]);

  const preview = useMemo(
    () => previewFromDraft(draftDetailToPreviewRecord(draftDetail) || {}),
    [draftDetail]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Show
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={viewPillClass(viewFilter === VIEW_ACTIVE)}
                onClick={() => setViewFilter(VIEW_ACTIVE)}
              >
                Active
              </button>
              <button
                type="button"
                className={viewPillClass(viewFilter === VIEW_IGNORED)}
                onClick={() => setViewFilter(VIEW_IGNORED)}
              >
                Ignored
              </button>
              <button
                type="button"
                className={viewPillClass(viewFilter === VIEW_ALL)}
                onClick={() => setViewFilter(VIEW_ALL)}
              >
                All
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Sort
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={sortPillClass(sortMode === SORT_UPDATED_DESC)}
                onClick={() => setSortMode(SORT_UPDATED_DESC)}
              >
                Recently updated
              </button>
              <button
                type="button"
                className={sortPillClass(sortMode === SORT_CREATED_DESC)}
                onClick={() => setSortMode(SORT_CREATED_DESC)}
              >
                Newest created
              </button>
              <button
                type="button"
                className={sortPillClass(sortMode === SORT_TITLE_ASC)}
                onClick={() => setSortMode(SORT_TITLE_ASC)}
              >
                Title A–Z
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="relative shrink-0">
              <button
                type="button"
                ref={bulkBtnRef}
                disabled={selectedCount === 0}
                onClick={() => setBulkOpen((o) => !o)}
                className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm font-medium shadow-sm ${
                  selectedCount === 0
                    ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-600"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                Bulk actions
                <svg className="w-4 h-4 opacity-70" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                </svg>
              </button>
              <FixedDropdown
                show={bulkOpen && selectedCount > 0}
                onClose={() => setBulkOpen(false)}
                triggerRef={bulkBtnRef}
                menuRef={bulkMenuRef}
                estimatedHeight={bulkMenuHeight}
                align="left"
              >
                {viewFilter === VIEW_IGNORED ? (
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900 disabled:opacity-50"
                    disabled={bulkMenuBusy || selectedUnignoreCount === 0}
                    onClick={() => openBulkConfirm(BULK_CONFIRM_RESTORE)}
                  >
                    {`Restore ${selectedUnignoreCount} ${selectedUnignoreCount === 1 ? "item" : "items"}`}
                  </button>
                ) : null}

                {viewFilter !== VIEW_IGNORED && selectedPublishCount > 0 ? (
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900 disabled:opacity-50"
                    disabled={bulkMenuBusy}
                    onClick={() => openBulkConfirm(BULK_CONFIRM_PUBLISH)}
                  >
                    {`Publish ${selectedPublishCount} ${selectedPublishCount === 1 ? "item" : "items"}`}
                  </button>
                ) : null}

                {viewFilter === VIEW_ACTIVE && selectedPublishCount > 0 ? (
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900 disabled:opacity-50"
                    disabled={bulkMenuBusy}
                    onClick={() => openBulkConfirm(BULK_CONFIRM_PUBLISH_IGNORE_REST)}
                  >
                    Publish {selectedPublishCount} and ignore rest
                  </button>
                ) : null}

                {viewFilter !== VIEW_IGNORED && selectedIgnoreCount > 0 ? (
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900 disabled:opacity-50"
                    disabled={bulkMenuBusy}
                    onClick={() => openBulkConfirm(BULK_CONFIRM_IGNORE)}
                  >
                    {`Ignore ${selectedIgnoreCount} ${selectedIgnoreCount === 1 ? "item" : "items"}`}
                  </button>
                ) : null}

                {viewFilter === VIEW_ALL && selectedUnignoreCount > 0 ? (
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900 disabled:opacity-50"
                    disabled={bulkMenuBusy}
                    onClick={() => openBulkConfirm(BULK_CONFIRM_RESTORE)}
                  >
                    {`Restore ${selectedUnignoreCount} ${selectedUnignoreCount === 1 ? "item" : "items"}`}
                  </button>
                ) : null}
              </FixedDropdown>
            </div>

          <div className="w-full sm:max-w-md min-w-0">
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Search drafts
            </label>
            <input
              type="search"
              className="form-input w-full"
              placeholder="Title, brand, SKU, or UPC"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
          </div>
          </div>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
          <SecondaryButton onClick={() => setUpcCsvOpen(true)}>Upload UPC CSV</SecondaryButton>
          <p className="text-sm text-slate-600 dark:text-slate-400">{summary}</p>
        </div>
      </div>

      <DraftProductsUpcCsvModal open={upcCsvOpen} onClose={() => setUpcCsvOpen(false)} />

      <BulkPublishProgressBanner
        jobId={activePublishJobId}
        job={activePublishJob}
        pollError={publishPollError}
        isActive={publishJobActive}
        collapsed={publishBannerCollapsed}
        onToggleCollapsed={() => setPublishBannerCollapsed((c) => !c)}
        onDismiss={dismissPublishJob}
      />

      {error && (
        <div className="rounded border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="relative min-h-[200px]">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-slate-900/50 rounded">
            <Loader />
          </div>
        )}
        <div className="overflow-x-auto rounded-sm border border-slate-200 dark:border-slate-700">
          <table className="w-full table-auto text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300">
              <tr>
                <th className="pl-4 pr-2 py-3 w-px">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600"
                    checked={allVisibleSelected && visibleIds.length > 0}
                    onChange={toggleSelectAllVisible}
                    disabled={visibleIds.length === 0 || loading}
                    aria-label="Select all on this page"
                  />
                </th>
                <th className="px-4 py-3 font-semibold w-14"> </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">SKU</th>
                <th className="px-4 py-3 font-semibold min-w-[14rem]">Product</th>
                <th className="px-4 py-3 font-semibold min-w-[8rem]">Brand</th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">UPC</th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Category</th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    {viewFilter === VIEW_IGNORED
                      ? "No ignored drafts. Ignored items appear here after you skip them from the active list."
                      : viewFilter === VIEW_ACTIVE
                        ? "No active draft products match this filter."
                        : "No draft products match this filter."}
                  </td>
                </tr>
              )}
              {items.map((row) => {
                const imageUrl = draftProductImageUrl(row);
                const rowId = String(row.id || "").trim();
                const selectable = isDraftSelectable(row);
                const statusLabel = publishStatusLabel(row.publish_status);
                const ignored = Boolean(row.ignored);
                return (
                  <tr
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    className={`bg-white dark:bg-theme-surface-accent hover:bg-slate-50/80 dark:hover:bg-slate-800/40 cursor-pointer ${
                      ignored ? "opacity-80" : ""
                    }`}
                    onClick={() => handleRowClick(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleRowClick(row);
                      }
                    }}
                  >
                    <td
                      className="pl-4 pr-2 py-3 align-middle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600"
                        checked={selectedKeys.has(rowId)}
                        onChange={() => toggleOne(rowId)}
                        disabled={!rowId || !selectable}
                        aria-label={`Select ${productLabel(row)}`}
                      />
                    </td>
                    <td className="px-4 py-3 align-middle">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt=""
                          className="h-10 w-10 rounded object-cover bg-slate-100 dark:bg-slate-800"
                        />
                      ) : (
                        <div
                          className="h-10 w-10 rounded bg-slate-100 dark:bg-slate-800"
                          aria-hidden
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-200 whitespace-nowrap">
                      {row.sku_id || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                      {productLabel(row)}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                      {row.company || "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-700 dark:text-slate-200 whitespace-nowrap">
                      {row.upc_code || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200 whitespace-nowrap">
                      {row.type || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200 whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        <span>{formatTimestamp(row.updated || row.created)}</span>
                        {ignored ? (
                          <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                            Ignored
                          </span>
                        ) : null}
                        {statusLabel ? (
                          <span
                            className={`text-xs font-medium ${publishStatusClass(row.publish_status)}`}
                          >
                            {statusLabel}
                          </span>
                        ) : null}
                        {ignored ? (
                          <button
                            type="button"
                            className="text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:underline text-left w-fit"
                            onClick={(e) => handleUnignoreOne(row, e)}
                            disabled={bulkIgnoreLoading}
                          >
                            Restore
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mb-4">
          <PaginationNumeric
            currentPage={page - 1}
            onSelect={(zeroBasedPage) => setPage(zeroBasedPage + 1)}
            totalPages={totalPages}
            isFirstPage={page <= 1}
            isLastPage={page >= totalPages}
          />
        </div>
      )}

      <ModalBasic
        id="master-menu-draft-preview"
        title="Draft product"
        modalOpen={previewOpen}
        setModalOpen={(open) => {
          if (!open) closePreviewModal();
        }}
        width="4xl"
      >
        {detailLoading ? (
          <div className="flex min-h-[240px] items-center justify-center">
            <Loader />
          </div>
        ) : detailError ? (
          <div className="rounded border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
            {detailError}
          </div>
        ) : draftDetail ? (
          <div className="space-y-6">
            {draftDetail.ignored ? (
              <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
                This draft is ignored and hidden from the active list. Restore it to publish or edit.
              </div>
            ) : null}
            <MockListingPreview preview={preview} />
            <div className="flex flex-wrap justify-end gap-2">
              <SecondaryButton type="button" onClick={handleManualEdit}>
                Manually Edit
              </SecondaryButton>
              {draftDetail.ignored ? (
                <PrimaryButton
                  type="button"
                  onClick={() => setBulkConfirm({ type: BULK_CONFIRM_RESTORE, rowOverride: draftDetail })}
                  disabled={bulkIgnoreLoading}
                >
                  Restore to active list
                </PrimaryButton>
              ) : (
                <PrimaryButton type="button" onClick={handlePublish} disabled={publishLoading}>
                  {publishLoading ? "Publishing…" : "Publish"}
                </PrimaryButton>
              )}
            </div>
          </div>
        ) : null}
      </ModalBasic>

      <ModalBasic
        id="master-menu-draft-bulk-confirm"
        title={bulkConfirmMeta?.title || "Confirm bulk action"}
        modalOpen={Boolean(bulkConfirm && bulkConfirmMeta)}
        setModalOpen={(open) => {
          if (!open) closeBulkConfirm();
        }}
        width="lg"
      >
        {bulkConfirmMeta ? (
          <div className="space-y-6">
            {bulkConfirmMeta.body}
            <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <SecondaryButton
                type="button"
                onClick={closeBulkConfirm}
                disabled={bulkConfirmBusy}
              >
                Cancel
              </SecondaryButton>
              {bulkConfirmMeta.confirmVariant === "danger" ? (
                <DangerButton
                  type="button"
                  onClick={executeBulkConfirm}
                  disabled={bulkConfirmIds.length === 0 || bulkConfirmMeta.canConfirm === false}
                  loading={bulkConfirmBusy}
                >
                  {bulkConfirmMeta.primaryLabel}
                </DangerButton>
              ) : (
                <PrimaryButton
                  type="button"
                  onClick={executeBulkConfirm}
                  disabled={bulkConfirmIds.length === 0 || bulkConfirmMeta.canConfirm === false}
                  loading={bulkConfirmBusy}
                >
                  {bulkConfirmMeta.primaryLabel}
                </PrimaryButton>
              )}
            </div>
          </div>
        ) : null}
      </ModalBasic>

      <ModalBasic
        id="master-menu-draft-edit"
        title="Edit draft product"
        modalOpen={editOpen}
        setModalOpen={(open) => {
          if (!open) closeEditModal();
        }}
        width="7xl"
        className="h-[92vh] sm:h-[92vh]"
      >
        {editOpen && draftDetail ? (
          <MasterMenuCreateProduct
            embedded
            draftEditMode
            draftProductId={draftDetail.id}
            initialDraftRecord={draftDetail}
            onClose={closeEditModal}
            onPublished={handlePublished}
          />
        ) : null}
      </ModalBasic>
    </div>
  );
};

export default MasterMenuDraftProducts;
